import { pluralize } from "@common/helpers/string";
import { Amount } from "@design-system/Amount/Amount";
import React, { useMemo } from "react";
import styles from "./BalanceSheetCheck.module.scss";
import { AccountProxyObject } from "./types";
import { ValidationResult } from "./validation";

interface Props {
    accounts: AccountProxyObject[];
    validationResult: ValidationResult;
}

export const BalanceSheetCheck: React.FC<Props> = ({
    accounts,
    validationResult,
}) => {
    const {
        errors: validationErrors,
        display: { balanceSheetTotals },
    } = validationResult;

    const accountSummary = useMemo(() => {
        const accountsWithName = accounts.filter(
            (account) => account.name?.trim() !== "",
        );

        return {
            total: accountsWithName.length,
            totals: balanceSheetTotals,
        };
    }, [accounts, balanceSheetTotals]);

    if (accountSummary.total === 0 || !accountSummary.totals) {
        return null;
    }

    const isBalanced = !validationErrors.balanceSheet;

    return (
        <div className={styles.balanceCheck}>
            <div className={styles.row}>
                <span className={styles.label}>
                    Total Assets{" "}
                    <span className={styles.accountCount}>
                        ({accountSummary.totals.assetsCount}{" "}
                        {pluralize(
                            "account",
                            accountSummary.totals.assetsCount,
                        )}
                        )
                    </span>
                </span>
                <span className={styles.value}>
                    <Amount amount={accountSummary.totals.totalAssets} />
                </span>
            </div>
            <div className={styles.row}>
                <span className={styles.label}>
                    Total Liabilities{" "}
                    <span className={styles.accountCount}>
                        ({accountSummary.totals.liabilitiesCount}{" "}
                        {pluralize(
                            "account",
                            accountSummary.totals.liabilitiesCount,
                        )}
                        )
                    </span>{" "}
                    + Equity{" "}
                    <span className={styles.accountCount}>
                        ({accountSummary.totals.equityCount}{" "}
                        {pluralize(
                            "account",
                            accountSummary.totals.equityCount,
                        )}
                        )
                    </span>
                </span>
                <span className={styles.value}>
                    <Amount
                        amount={accountSummary.totals.liabilitiesPlusEquity}
                    />
                </span>
            </div>
            {!isBalanced && (
                <div className={styles.differenceSection}>
                    <span className={styles.differenceLabel}>
                        Difference{" "}
                        <span className={styles.differenceSubtitle}>
                            (Assets must equal Liabilities + Equity)
                        </span>
                    </span>
                    <span className={styles.differenceValue}>
                        <Amount
                            amount={Math.abs(accountSummary.totals.difference)}
                        />
                    </span>
                </div>
            )}
        </div>
    );
};
